'use client'

import type { ReactNode } from 'react'
import * as Sentry from '@sentry/nextjs'
import { Component } from 'react'

interface Props {
  children: ReactNode
  fallback?: React.ComponentType<{ error: Error, retry: () => void }>
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void
  maxRetries?: number
}

interface State {
  hasError: boolean
  error: Error | null
  retryCount: number
}

/**
 * Sentry 错误边界组件
 * 捕获子组件树中的 JavaScript 错误，记录错误信息，并显示降级 UI
 * 直接使用 Sentry API，无需依赖全局实例
 */
export class SentryErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      retryCount: 0,
    }

    // 绑定方法到实例，避免每次渲染创建新函数
    this.handleRetry = this.handleRetry.bind(this)
    this.handleReload = this.handleReload.bind(this)
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    const { onError } = this.props

    // 调用自定义错误处理函数
    if (onError) {
      try {
        onError(error, errorInfo)
      }
      catch (onErrorError) {
        console.warn('Error in onError callback:', onErrorError)
      }
    }

    // 直接使用 Sentry API 上报错误
    try {
      Sentry.captureException(error, {
        contexts: {
          react: {
            componentStack: errorInfo.componentStack,
            retryCount: this.state.retryCount,
            errorBoundary: 'SentryErrorBoundary',
          },
        },
        tags: {
          error_boundary: 'SentryErrorBoundary',
          retry_count: this.state.retryCount.toString(),
          environment: process.env.NODE_ENV || 'unknown',
        },
      })
    }
    catch (sentryError) {
      console.warn('Failed to report error to Sentry:', sentryError)
    }
  }

  handleRetry = (): void => {
    const { maxRetries = 3 } = this.props
    const { retryCount } = this.state

    if (retryCount < maxRetries) {
      this.setState(prevState => ({
        hasError: false,
        error: null,
        retryCount: Math.min(prevState.retryCount + 1, maxRetries),
      }))
    }
  }

  handleReload = (): void => {
    window.location.reload()
  }

  render(): ReactNode {
    const { children, fallback: Fallback, maxRetries = 3 } = this.props
    const { hasError, error, retryCount } = this.state

    if (hasError && error) {
      // 使用自定义降级组件
      if (Fallback) {
        return <Fallback error={error} retry={this.handleRetry} />
      }

      // 默认降级 UI
      return (
        <div
          className="flex min-h-[200px] flex-col items-center justify-center rounded-lg bg-red-50 p-6 text-center"
          role="alert"
          aria-live="polite"
        >
          <div className="mb-4 text-2xl" aria-hidden="true">⚠️</div>
          <h2 className="mb-2 text-lg font-semibold text-red-800">
            Something went wrong
          </h2>
          <p className="mb-4 text-sm text-red-600">
            {error.message || 'An unexpected error occurred'}
          </p>

          {retryCount < maxRetries
            ? (
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={this.handleRetry}
                    aria-label={`Retry (attempt ${retryCount + 1} of ${maxRetries})`}
                    className="rounded bg-red-600 px-4 py-2 text-white transition-colors hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    Try again (
                    {retryCount + 1}
                    /
                    {maxRetries}
                    )
                  </button>
                  <p className="text-xs text-red-500">
                    Retry attempt
                    {' '}
                    {retryCount + 1}
                    {' '}
                    of
                    {' '}
                    {maxRetries}
                  </p>
                </div>
              )
            : (
                <div className="space-y-2">
                  <p className="text-sm text-red-500">
                    Maximum retry attempts reached
                  </p>
                  <button
                    type="button"
                    onClick={this.handleReload}
                    aria-label="Reload page"
                    className="rounded bg-gray-600 px-4 py-2 text-white transition-colors hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                    Reload page
                  </button>
                </div>
              )}

          {/* 开发环境显示详细错误信息 */}
          {process.env.NODE_ENV === 'development' && (
            <details className="mt-4 text-left">
              <summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-800">
                Show error details (dev only)
              </summary>
              <pre className="mt-2 max-h-32 overflow-auto rounded bg-gray-100 p-2 text-xs text-gray-700">
                {error.stack}
              </pre>
            </details>
          )}
        </div>
      )
    }

    return children
  }
}

/**
 * 高阶组件：为组件添加错误边界
 */
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<Props, 'children'>,
) {
  const WrappedComponent = (props: P) => (
    <SentryErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </SentryErrorBoundary>
  )

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`

  return WrappedComponent
}

export default SentryErrorBoundary
